import React, { useState, useMemo } from 'react';
import {
  LayoutDashboard, PlusCircle, ListTodo, UserCheck, ShieldCheck,
  ChevronLeft, ChevronRight, Bot, History, BarChart3, Settings,
  LogOut,
} from 'lucide-react';
import { ROLES } from './constants';
import { useTaskStore } from './store/useTaskStore';
import Dashboard from './components/Dashboard';
import CreateTask from './components/CreateTask';
import TaskList from './components/TaskList';
import MyTasks from './components/MyTasks';
import LeoReview from './components/LeoReview';
import TaskDetail from './components/TaskDetail';
import OperationLogs from './components/OperationLogs';
import ChartsPage from './components/ChartsPage';
import SettingsPage from './components/SettingsPage';

/* ================================================================
   亚马逊AI运营中台 V2 — 任务中心 Task Hub
   ================================================================ */

export default function TaskHubApp() {
  const [currentUser, setCurrentUser] = useState('当前用户');
  const [userRoleKey, setUserRoleKey] = useState('admin');
  const userRole = ROLES[userRoleKey];
  const [page, setPage] = useState('dashboard');
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const store = useTaskStore(currentUser, userRole);
  const { tasks, logs, stats, updateTask, addTask, deleteTask, batchUpdateStatus, batchAssign, batchArchive, addComment, addApproval, changeStatus, createTask } = store;

  const selectedTask = useMemo(() => tasks.find(t => t.id === selectedTaskId), [tasks, selectedTaskId]);

  function navigateTo(target, payload = null) {
    if (typeof payload === 'string') {
      setSelectedTaskId(payload);
    } else if (payload && typeof payload === 'object') {
      // 处理来自dashboard的筛选跳转
    }
    setPage(target);
  }

  const navItems = [
    { key: 'dashboard', label: '概览看板', icon: LayoutDashboard, visible: true },
    { key: 'create', label: '创建任务', icon: PlusCircle, visible: true },
    { key: 'all', label: '全部任务', icon: ListTodo, visible: true },
    { key: 'mine', label: '我的任务', icon: UserCheck, visible: true },
    { key: 'leo', label: '审批中心', icon: ShieldCheck, visible: userRole?.canApprove },
    { key: 'logs', label: '操作日志', icon: History, visible: true },
    { key: 'charts', label: '统计图表', icon: BarChart3, visible: true },
    { key: 'settings', label: '系统配置', icon: Settings, visible: userRole?.canConfigLLM },
  ].filter(n => n.visible);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex">
      {/* 侧边栏 */}
      <aside className={`${collapsed ? 'w-16' : 'w-56'} bg-slate-900 text-white flex flex-col transition-all duration-300 shrink-0`}>
        <div className="h-14 flex items-center px-4 border-b border-slate-700">
          <Bot className="w-6 h-6 text-indigo-400 shrink-0" />
          {!collapsed && <span className="ml-3 font-semibold text-sm truncate">亚马逊AI运营中台</span>}
        </div>
        <nav className="flex-1 py-4 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const active = page === item.key;
            return (
              <button
                key={item.key}
                onClick={() => navigateTo(item.key)}
                className={`w-full flex items-center px-3 py-2.5 text-sm transition-colors ${active ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!collapsed && <span className="ml-3 truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-700 space-y-2">
          {!collapsed && (
            <div className="px-3 py-2">
              <div className="text-xs text-slate-400 mb-1">当前角色</div>
              <select
                className="w-full text-xs bg-slate-800 text-white border border-slate-600 rounded px-2 py-1"
                value={userRoleKey}
                onChange={e => setUserRoleKey(e.target.value)}
              >
                <option value="admin">管理员</option>
                <option value="writer">写作人</option>
                <option value="reviewer">审批人</option>
              </select>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 text-slate-400 hover:text-white"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </aside>

      {/* 主内容 */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* 顶部栏 */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 shrink-0">
          <div className="flex items-center space-x-2 text-sm text-slate-500">
            <span className="font-medium text-slate-800">任务中心</span>
            <ChevronRight className="w-4 h-4" />
            <span>{navItems.find(n => n.key === page)?.label || '详情'}</span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-slate-600">
              <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-xs">
                {currentUser.slice(0, 1)}
              </div>
              <span>{currentUser}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500">{userRole?.label}</span>
            </div>
          </div>
        </header>

        {/* 页面内容 */}
        <div className="flex-1 overflow-auto p-6">
          {page === 'dashboard' && <Dashboard tasks={tasks} navigateTo={navigateTo} currentUser={currentUser} stats={stats} />}
          {page === 'create' && <CreateTask onCreate={createTask} navigateTo={navigateTo} currentUser={currentUser} />}
          {page === 'all' && (
            <TaskList
              tasks={tasks}
              navigateTo={navigateTo}
              currentUser={currentUser}
              userRole={userRole}
              deleteTask={deleteTask}
              batchUpdateStatus={batchUpdateStatus}
              batchAssign={batchAssign}
              batchArchive={batchArchive}
            />
          )}
          {page === 'mine' && (
            <MyTasks
              tasks={tasks}
              navigateTo={navigateTo}
              currentUser={currentUser}
              userRole={userRole}
              deleteTask={deleteTask}
              batchUpdateStatus={batchUpdateStatus}
              batchAssign={batchAssign}
              batchArchive={batchArchive}
            />
          )}
          {page === 'leo' && <LeoReview tasks={tasks} navigateTo={navigateTo} currentUser={currentUser} addApproval={addApproval} />}
          {page === 'detail' && selectedTask && (
            <TaskDetail
              task={selectedTask}
              navigateTo={navigateTo}
              currentUser={currentUser}
              userRole={userRole}
              updateTask={updateTask}
              deleteTask={deleteTask}
              addComment={addComment}
              addApproval={addApproval}
              changeStatus={changeStatus}
            />
          )}
          {page === 'logs' && <OperationLogs logs={logs} tasks={tasks} />}
          {page === 'charts' && <ChartsPage tasks={tasks} navigateTo={navigateTo} />}
          {page === 'settings' && <SettingsPage userRole={userRole} />}
        </div>
      </main>
    </div>
  );
}
